import axios, { AxiosError, AxiosResponse } from 'axios';
import _, { isNumber } from 'underscore';

import { ServerFilterClauses } from '~commonInfra/utils/ServerFilterClauses';
import { CollectionsErrorCode } from '~src/commonInfra/utils/ErrorCodes';
import { err, ok, Result } from '~src/commonInfra/utils/Result';
import { MarkViewedRequest, ViewedObjectType } from '~src/spacesPlatform/serverApi/requests/MarkViewedRequest';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';

import { ListResult } from '../../httpClient/fetchers/ListResult';
import { BaseParamsBuilderResourceQuery } from '../../httpClient/resources/ResourceQuery';
import WebHttpClient from '../../httpClient/WebHttpClient';
import {
  FilterFieldNames,
  FilterOps,
  IFieldFilterClause,
  ILeafFilterClause,
  IStringValuedFieldFilterClause,
  IStringValuedFilterClause,
  IValuedFieldFilterClause,
} from '../../spacesPlatform/models/IFilterClause';
import { CollectionMapper } from '../mappers/CollectionMapper';
import {
  Collection,
  CollectionError,
  CollectionVisibility,
  DeleteCollectionData,
  UpdateCollectionsResponseData,
} from '../space/CollectionModels';
import { ICollectionInfo } from '../space/ICollectionInfo';
import { IGetCollectionResult } from '../space/IGetCollectionResult';
import { COLLECTIONS_PATH, collectionsAxiosRequestConfig, CollectionsListResponse, FilterOpsToString } from './CollectionResourceQueryHelpers';

export interface GetCollectionResponse {
  readonly collection: Collection;
  readonly errors: CollectionError[];
}

export interface CreateCollectionResponse extends Collection {}

export interface GetCollectionsResponse extends CollectionsListResponse<Collection> {
}

export interface UpdateCollectionData {
  readonly luid: string;
  readonly name?: string;
  readonly description?: string;
  readonly visibility?: CollectionVisibility;
  readonly ownerLuid?: string;
}

const DELETE_COLLECTIONS_BLOCK_SIZE = 50;
export interface DeleteResponse {
  readonly response: string[];
  readonly errors: CollectionError[];
}

export const UPDATE_COLLECTIONS_LIMIT_HEADER = 'collections-batch-update-limit';
export interface UpdateResponse {
  readonly response: string[];
  readonly errors: CollectionError[];
}

function replaceIsFavoriteFilterForCollections(filters: ILeafFilterClause[]): ILeafFilterClause[] {
  if (!filters || filters.length === 0) {
    return filters;
  }

  let isFavoriteFilterFn: (clause: ILeafFilterClause)  => boolean =
    (clause: ILeafFilterClause) =>
      clause.operator === FilterOps.eq && (clause as IFieldFilterClause).field === FilterFieldNames.isFavorite;

  let isFavoriteFilter = filters.find((clause) => isFavoriteFilterFn(clause)) as IValuedFieldFilterClause<boolean>;
  if (!isFavoriteFilter) {
    // didn't find a isFavorites filter so no manipulation needed
    return filters;
  }

  // remove isFavorite
  let updatedFilters: ILeafFilterClause[] = filters.filter((clause) => !isFavoriteFilterFn(clause));
  // add it back in but correct for collections
  updatedFilters.push(ServerFilterClauses.serverFilterClauses.favoriteCollectionsFilter(isFavoriteFilter.value + ''));
  return updatedFilters;
}

// add a call to orderCollections here TFS1302371
export class CollectionsResourceQuery extends BaseParamsBuilderResourceQuery<ICollectionInfo> {
  public getSlice(start: number, size: number): Promise<ListResult<ICollectionInfo>> {
    let filters: ILeafFilterClause[] = this.getFilters();
    const matchesFilter = filters.find((clause) => clause.operator === FilterOps.matches);

    // If we are going to Collections Service, 'isFavorite' needs to be replaced with 'favorite' to conform to AIPs
    filters = replaceIsFavoriteFilterForCollections(filters);

    let queryParams: {search?: string, sort?: string, filter?: string, start_index?: string, max_items?: string} = {};

    const order: IOrderClause[] = this.getOrder();
    if (order.length > 0) {
      queryParams.sort = order
          .map((clause) => clause.field + ' ' + (clause.ascending ? 'asc' : 'desc'))
          .join(',');
    }

    if (filters.length > 0) {
      const filtersWithoutMatches = _.without(filters, matchesFilter);
      if (filtersWithoutMatches.length > 0) {
        queryParams.filter =
          filtersWithoutMatches
            .map((clause) => {
              const valueClause = clause as IStringValuedFieldFilterClause;
              return valueClause.field + FilterOpsToString(valueClause.operator) + '"' + valueClause.value + '"';
            })
            .join(' AND ');
      }
    }

    if (matchesFilter) {
      const searchValue = encodeURIComponent((matchesFilter as IStringValuedFilterClause).value);
      if (searchValue) {
        queryParams.search = searchValue;
      }
    }

    if (start != null) {
      queryParams.start_index = '' + start;
    }
    if (size != null) {
      queryParams.max_items = '' + size;
    }

    let queryString = '';
    if (!_.isEmpty(queryParams)) {
      queryString = '?';
      queryString += _.pairs(queryParams).map((value) => value[0]+'='+value[1]).join('&');
    }

    const url = COLLECTIONS_PATH + queryString;
    const requestConfig = collectionsAxiosRequestConfig('GET', url);
    return axios.request<GetCollectionsResponse>(requestConfig).then((response: AxiosResponse<GetCollectionsResponse>) => {
      return CollectionMapper.mapList(response, start);
    })
    .catch(() => {
      return ListResult.empty<ICollectionInfo>();
    });
  }

  private disableDelay = false;
  public forTestOnly_disableDelay(disable = true) {
    // Meant for test use only
    this.disableDelay = disable;
    return this;
  }

  public addDelay<T>(result: Promise<T>): Promise<T> {
    if (this.disableDelay) {
      return result;
    }

    return result.then((value: T) => {
      return new Promise(function(resolve) {
        setTimeout(resolve.bind(null, value), 2000);
      });
    });
  }

  public create(name: string): Promise<ICollectionInfo> {
    const requestConfig = collectionsAxiosRequestConfig('POST', COLLECTIONS_PATH, { name, desc: '' });
    return axios.request<CreateCollectionResponse>(requestConfig)
      .then(CollectionMapper.mapsSingle)
      .catch((error) => Promise.reject(error.response));
  }

  private deleteCollectionsBlock = DELETE_COLLECTIONS_BLOCK_SIZE;
  public forTestOnly_setDeleteCollectionsBlock(block: number) {
    // Meant for unit tests only
    this.deleteCollectionsBlock = block;
    return this;
  }

  public delete(luids: string[]): Promise<DeleteCollectionData> {
    const promises = _.chunk(luids, this.deleteCollectionsBlock).map((luidsChunk: string[]) => {
      return this.deleteHelper(luidsChunk);
    });

    const allPromises = Promise.all(promises).then((results: DeleteCollectionData[]) => {
      return _.reduce(results, (memo, result: DeleteCollectionData) => {
        return {
          errors: memo.errors.concat(result.errors),
          successfulLuids: memo.successfulLuids.concat(result.successfulLuids)
        };
      }, {errors: [], successfulLuids: []});
    });

    //adds a 2000 ms delay for deleting so the backend has time to catch up
    return this.addDelay(allPromises);
  }

  private deleteHelper(luids: string[]): Promise<DeleteCollectionData>  {
    const luidsParam = luids.join(',');

    const url = COLLECTIONS_PATH + '?luids=' + luidsParam;
    const requestConfig = collectionsAxiosRequestConfig('DELETE', url);
    return axios.request(requestConfig)
      .then(CollectionMapper.mapDeleteCollectionsResponse)
      .catch((error) => Promise.reject(error.data));
  }

  public update(updateData: UpdateCollectionData[]): Promise<UpdateCollectionsResponseData> {
    const responsePromise = this.updateHelper(updateData)
      .then(response => response)
      .catch((error: AxiosError<UpdateResponse>) => {
        return this.tooManyCollectionsError(error).map(batchLimit => {
          const chunks = _.chunk(updateData, batchLimit);
          const allResults: UpdateCollectionsResponseData = {
            successfulLuids: [],
            errors: []
          };

          return chunks.reduce((promiseChain, chunk) => {
            return promiseChain.then(() => {
              return this.updateHelper(chunk).then(res => {
                allResults.successfulLuids.push(...(res.successfulLuids || []));
                allResults.errors.push(...(res.errors || []));
              }).catch((error: AxiosError<UpdateResponse>) => {
                allResults.successfulLuids.push(...(error.response.data.response || []));
                allResults.errors.push(...(error.response.data.errors || []));
              });
            });
          }, Promise.resolve()).then(() => allResults);
        }).unwrapOr(Promise.reject(error.response));
      });

    //adds a 2000 ms delay for updating name, description, and owner luid so the backend has time to catch up
    if(updateData[0].description || updateData[0].name || updateData[0].ownerLuid) {
      return this.addDelay(responsePromise);
    }

    return responsePromise;
  }

  private updateHelper(updateData: UpdateCollectionData[]): Promise<UpdateCollectionsResponseData> {
    const url = COLLECTIONS_PATH + '/batchUpdate';
    const requestConfig = collectionsAxiosRequestConfig('POST', url, updateData);
    return axios.request<UpdateResponse>(requestConfig)
      .then(CollectionMapper.mapUpdateCollectionsResponse)
      .catch(error => Promise.reject(error));
  }

  private tooManyCollectionsError(error: AxiosError<unknown>): Result<number, Error> {
    return this.getCollectionsErrorCode(error).then(errorCode => {
      if (errorCode === CollectionsErrorCode.TOO_MANY_COLLECTIONS) {
        const batchLimit = parseInt(error.response.headers[UPDATE_COLLECTIONS_LIMIT_HEADER], 10);
        if (!batchLimit || isNaN(batchLimit)) {
          return err(new Error('Batch limit header missing or invalid.'));
        }
        return ok(batchLimit);
      }
      return err(error);
    });
  }

  private getCollectionsErrorCode(error: AxiosError<any>): Result<number, Error> {
    if (error?.response?.data?.errorCode && isNumber(error.response.data.errorCode)) {
      return ok(error.response.data.errorCode);
    }

    if (error?.response?.data?.errors?.length > 0) {
      return ok(error.response.data.errors[0].errorCode);
    }

    return err(error);
  }

  public getByLuid(luid: string): Promise<IGetCollectionResult> {
    const url = COLLECTIONS_PATH + '/' + luid;
    const requestConfig = collectionsAxiosRequestConfig('GET', url);
    return axios.request<GetCollectionResponse>(requestConfig)
      .then(CollectionMapper.mapGetCollectionResult)
      .catch((error) => Promise.reject(error.response));
  }

  public getCount(): Promise<number> {
    // Grabs first slice of Collections to determine if a Collection exists to direct users to different UX flows
    return this.getSlice(0, 1).then((slice: ListResult<ICollectionInfo>) => slice.total);
  }

  public getFirst(): Promise<ICollectionInfo> {
    throw new Error('CollectionsResourceQuery getFirst not implemented');
  }
}

export class CollectionsUsageStats {
  public static markViewed(id: string) {
    return WebHttpClient.sendRequest(new MarkViewedRequest({
      objectId: id,
      objectType: ViewedObjectType.Collection
    }));
  }
}